import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface User {
  id: string;
  username: string;
  email: string;
  depositAddress: string;
  balance: number; // Balance in lamports (converted from string by UserService)
  totalWagered: number;
  totalWon: number;
  totalLost: number;
  isAdmin: boolean;
  createdAt: string;
  lastActive: string;
  profilePicture?: string | null;
  settings: {
    dailyLossLimit: number;
    sessionTimeLimit: number;
    realityCheckInterval: number;
  };
}

interface AuthState {
  user: User | null;
  token: string | null;
  isConnected: boolean;
  isLoading: boolean;
  error: string | null;
}

// Load persisted auth state from localStorage
const loadPersistedState = (): AuthState => {
  try {
    const token = localStorage.getItem('authToken');
    const userJson = localStorage.getItem('authUser');
    
    if (token && userJson) {
      const user = JSON.parse(userJson);
      return {
        user,
        token,
        isConnected: true,
        isLoading: false,
        error: null,
      };
    }
  } catch (error) {
    console.error('Error loading persisted auth state:', error);
    localStorage.removeItem('authToken');
    localStorage.removeItem('authUser');
  }
  
  return {
    user: null,
    token: null,
    isConnected: false,
    isLoading: false,
    error: null,
  };
};

const initialState: AuthState = loadPersistedState();

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    loginSuccess: (state, action: PayloadAction<{ user: User; token: string }>) => {
      state.user = action.payload.user;
      state.token = action.payload.token;
      state.isConnected = true;
      state.isLoading = false;
      state.error = null;
      
      // Persist to localStorage (but don't persist dynamic data like balance, xp, level to prevent stale data)
      localStorage.setItem('authToken', action.payload.token);
      
      const userWithoutDynamicData = { 
        ...action.payload.user, 
        balance: 0,
        totalWagered: 0,
        totalWon: 0,
        totalLost: 0
      }; // Don't persist dynamic data that changes frequently
      localStorage.setItem('authUser', JSON.stringify(userWithoutDynamicData));
    },
    logout: (state) => {
      state.user = null;
      state.token = null;
      state.isConnected = false;
      state.isLoading = false;
      state.error = null;
      
      // Clear from localStorage
      localStorage.removeItem('authToken');
      localStorage.removeItem('authUser');
    },
    updateUser: (state, action: PayloadAction<Partial<User>>) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload };
        // Update localStorage but don't persist dynamic data to prevent stale data
        const userWithoutDynamicData = { 
          ...state.user, 
          balance: 0,
          totalWagered: 0,
          totalWon: 0,
          totalLost: 0
        };
        localStorage.setItem('authUser', JSON.stringify(userWithoutDynamicData));
      }
    },
    updateSettings: (state, action: PayloadAction<User['settings']>) => {
      if (state.user) {
        state.user.settings = action.payload;
        // Update localStorage but don't persist dynamic data to prevent stale data
        const userWithoutDynamicData = { 
          ...state.user, 
          balance: 0,
          totalWagered: 0,
          totalWon: 0,
          totalLost: 0
        };
        localStorage.setItem('authUser', JSON.stringify(userWithoutDynamicData));
      }
    },
    updateBalance: (state, action: PayloadAction<number>) => {
      if (state.user) {
        const newBalance = action.payload;
        const currentBalance = state.user.balance;
        
        // Only update if balance actually changed (avoid unnecessary updates)
        if (Math.abs(newBalance - currentBalance) > 0.000001) {
          state.user.balance = newBalance;
          // Don't update localStorage for balance - it should always come from API
        }
      }
    },
    // Clear authentication state when token is invalid
    clearAuth: (state) => {
      state.user = null;
      state.token = null;
      state.isConnected = false;
      state.isLoading = false;
      state.error = null;
      
      // Clear from localStorage
      localStorage.removeItem('authToken');
      localStorage.removeItem('authUser');
    },
  },
});

export const {
  setLoading,
  setError,
  loginSuccess,
  logout,
  updateUser,
  updateSettings,
  updateBalance,
  clearAuth,
} = authSlice.actions;

export default authSlice.reducer;